/**
 * useStatusWebSocket Hook
 *
 * WebSocket hook for real-time task and iteration status updates.
 * Connects to /ws/tasks/{userId} and receives status change notifications
 * when workers claim/complete/fail iterations.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export interface TaskStatusUpdate {
  id: string;
  status: string;
  title: string;
  updated_at: string;
}

export interface IterationStatusUpdate {
  id: string;
  task_id: string;
  status: string;
  updated_at: string;
}

export interface UseStatusWebSocketReturn {
  lastTaskUpdate: TaskStatusUpdate | null;
  lastIterationUpdate: IterationStatusUpdate | null;
  isConnected: boolean;
}

export function useStatusWebSocket(): UseStatusWebSocketReturn {
  const { user } = useAuth();
  const [lastTaskUpdate, setLastTaskUpdate] = useState<TaskStatusUpdate | null>(null);
  const [lastIterationUpdate, setLastIterationUpdate] = useState<IterationStatusUpdate | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    const connect = () => {
      if (!user?.id || !isMountedRef.current) return;

      // Close existing connection if any
      if (wsRef.current) {
        // Don't close if still connecting - just abandon it
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${protocol}//${window.location.host}/ws/tasks/${user.id}`;

      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!isMountedRef.current) {
            ws.close();
            return;
          }
          setIsConnected(true);

          // Start ping interval to keep connection alive
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000); // Ping every 30 seconds
        };

        ws.onclose = () => {
          if (!isMountedRef.current) return;
          setIsConnected(false);

          // Clear ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }

          // Reconnect after delay (if still mounted and user logged in)
          if (isMountedRef.current && user?.id) {
            reconnectTimeoutRef.current = setTimeout(connect, 3000);
          }
        };

        ws.onerror = () => {
          // Error is handled by onclose - suppress console noise
        };

        ws.onmessage = (event) => {
          if (!isMountedRef.current) return;
          try {
            const msg = JSON.parse(event.data);

            if (msg.type === 'task_update') {
              setLastTaskUpdate(msg.data);
            } else if (msg.type === 'iteration_update') {
              setLastIterationUpdate(msg.data);
            }
            // Ignore pong messages
          } catch (e) {
            // Silently ignore parse errors
          }
        };
      } catch {
        // Retry after delay if still mounted
        if (isMountedRef.current) {
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        }
      }
    };

    // Delay connection slightly to handle React strict mode double-mount
    connectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, 100);

    return () => {
      // Mark as unmounted first to prevent reconnection attempts
      isMountedRef.current = false;

      // Clear all timeouts
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Only close if connection is open (not connecting)
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [user?.id]);

  return { lastTaskUpdate, lastIterationUpdate, isConnected };
}

export default useStatusWebSocket;
